import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Sun, Moon, ArrowLeft, Globe } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/** LanguageSwitcher — видим глобус + код на активния език (BG / EN / ES). */
export function LanguageSwitcher({ isDark, compact = false, testIdPrefix = "lang" }) {
  const { language, setLanguage, SUPPORTED_LANGUAGES } = useLanguage();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Език / Language"
          data-testid={`${testIdPrefix}-switcher-trigger`}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-bold transition-colors ${
            isDark
              ? "border-white/15 text-slate-200 hover:bg-white/10"
              : "border-slate-200 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <Globe className="w-3.5 h-3.5" />
          {!compact && <span>{language.toUpperCase()}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`w-44 ${isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : ""}`}>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            data-testid={`${testIdPrefix}-switcher-${lang.code}`}
            className={`cursor-pointer gap-2 ${language === lang.code ? "text-[#00796B] font-semibold" : ""}`}
          >
            <span className="text-[11px] font-bold w-6">{lang.label}</span>
            <span className="text-sm">{lang.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SubpageNav() {
  const { language } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);

  return (
    <nav className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-[hsl(223,47%,6%)]/95 border-[hsl(223,47%,12%)]' : 'bg-white/95 border-slate-100'}`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      data-testid="subpage-nav">
      <div className="max-w-5xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
        <Link to="/" className="flex items-center">
          {/* iter1078 — на телефон само иконата, за да се чете ясно */}
          <img src="/app-icon.png" alt="Nutramilo" className="h-9 w-9 object-contain sm:hidden" data-testid="subpage-logo-mark" />
          <img src="/nutramilo-logo.png" alt="Nutramilo" className="hidden sm:block h-8 w-auto object-contain" onError={e => e.target.style.display = "none"} />
        </Link>
        <div className="flex items-center gap-1.5">
          <LanguageSwitcher isDark={isDark} testIdPrefix="subpage-lang" />
          <button onClick={toggleTheme} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`} data-testid="subpage-theme-toggle">
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <Link to="/dashboard"><Button size="sm" className="bg-gradient-to-r from-[#7B1FA2] to-[#F06292] hover:from-[#6A1B9A] hover:to-[#E91E63] text-white text-xs rounded-full px-5">{L("Моето здраве", "My health", "Mi salud")}</Button></Link>
          ) : (
            <>
              <Link to="/login" data-testid="subpage-nav-login"
                    className={`hidden sm:inline-flex items-center text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                      isDark ? "text-slate-300 hover:text-white hover:bg-white/5"
                             : "text-slate-700 hover:text-[#287a2e] hover:bg-slate-100"
                    }`}>
                {L("Вход", "Sign in", "Entrar")}
              </Link>
              <Link to="/register" data-testid="subpage-nav-register">
                <Button size="sm" className="bg-gradient-to-r from-[#7B1FA2] to-[#F06292] hover:from-[#6A1B9A] hover:to-[#E91E63] text-white text-xs rounded-full px-5">
                  {L("Регистрация", "Sign up", "Registrarse")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export function SubpageBackLink({ label, to = "/" }) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  // iter1089 — history-first back: use browser back if we have history to
  // pop, otherwise fall back to the hardcoded `to`. Solves the reported BHI
  // regression where back always jumped to /dashboard/health-intelligence
  // regardless of the actual previous screen.
  const handleBack = React.useCallback((e) => {
    e.preventDefault();
    const cameFromApp = typeof window !== "undefined"
      && window.history
      && window.history.length > 1
      && (document.referrer === "" || document.referrer.includes(window.location.host));
    if (cameFromApp) {
      navigate(-1);
    } else {
      navigate(to);
    }
  }, [navigate, to]);
  return (
    <Link
      to={to}
      onClick={handleBack}
      data-testid="subpage-back-link"
      className={`inline-flex items-center gap-1.5 text-xs font-medium mb-8 transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
    >
      <ArrowLeft className="w-3.5 h-3.5" /> {label}
    </Link>
  );
}

export function SubpageFooter() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);

  return (
    <footer
      data-testid="subpage-footer"
      className={`border-t py-12 ${isDark ? "border-[hsl(223,47%,12%)] bg-[hsl(223,47%,5%)]" : "border-slate-100 bg-white"}`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
        <div className="col-span-2">
          <img src="/nutramilo-logo.png" alt="Nutramilo" className="h-9 mb-3" onError={(e) => (e.target.style.display = "none")} />
          <p className={`text-xs leading-relaxed max-w-sm ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {L(
              "Метаболитна интелигентност, която ти показва защо тялото ти реагира така — и точно какво да промениш.",
              "Metabolic intelligence that shows you why your body reacts the way it does — and exactly what to change.",
              "Inteligencia metabólica que le muestra por qué su cuerpo reacciona así — y exactamente qué cambiar."
            )}
          </p>
          <p className={`text-xs mt-3 ${isDark ? "text-slate-600" : "text-slate-400"}`}>
            © {new Date().getFullYear()} International Sci Ink Press Ltd EOOD
          </p>
        </div>
        <div>
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{L("Продукт", "Product", "Producto")}</div>
          <ul className="space-y-1.5">
            <li><Link to="/pricing" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("Цени", "Pricing", "Precios")}</Link></li>
            <li><Link to="/tools" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("Инструменти", "Tools", "Herramientas")}</Link></li>
            <li><Link to="/assessment" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("Безплатна оценка", "Free assessment", "Evaluación gratuita")}</Link></li>
            <li><Link to="/technical" data-testid="subpage-footer-technical" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("Техническа спецификация", "Technical spec", "Especificación técnica")}</Link></li>
          </ul>
        </div>
        <div>
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{L("Ресурси", "Resources", "Recursos")}</div>
          <ul className="space-y-1.5">
            <li><Link to="/about" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("За нас", "About us", "Sobre nosotros")}</Link></li>
            <li><Link to="/consultants" data-testid="subpage-footer-consultants" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("Консултанти", "Consultants", "Consultores")}</Link></li>
            <li><Link to="/guide" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("Ръководство", "Guide", "Guía")}</Link></li>
            <li><Link to="/faq" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("ЧЗВ", "FAQ", "Preguntas frecuentes")}</Link></li>
          </ul>
        </div>
        <div>
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{L("Правни", "Legal", "Legal")}</div>
          <ul className="space-y-1.5">
            <li><Link to="/legal" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("GDPR и политики", "GDPR & policies", "RGPD y políticas")}</Link></li>
            <li><Link to="/refund" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("Връщане", "Refund", "Reembolso")}</Link></li>
            <li><Link to="/contact" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{L("Контакти", "Contact", "Contacto")}</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
